import { lazy } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { api } from 'common';

import { entityTestPath } from './EntityTest/path';
import { NotFoundPage } from './NotFoundPage';
import { LazyLoader } from '../components/LazyLoader';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { FeatureFlagKey, useFlag } from '../utils/flags';
import { HumanTaskAppVariant } from './Agents/Flows/HumanTasks/types';

const ApiInstance = lazy(() => import('./ApiInstance'));
const ApiInstanceV2Routes = lazy(() =>
  import('./ApiInstanceV2/Routes').then((module) => ({ default: module.ApiInstanceRoutes }))
);
const PracticeConfigApiInstance = lazy(() => import('./ApiInstance/PracticeConfig'));
const Assistants = lazy(() =>
  import('./Assistants/Routes').then((module) => ({
    default: module.AssistantRoutes,
  }))
);
const ContactStatusRoutes = lazy(() =>
  import('./ContactStatus/Routes').then((module) => ({ default: module.ContactStatusRoutes }))
);
const ConnectorRoutes = lazy(() =>
  import('./Connector/Routes').then((module) => ({ default: module.ConnectorRoutes }))
);
const Diagnostics = lazy(() => import('./Diagnostics'));
const DocumentTemplates = lazy(() => import('./DocumentTemplates'));
const EhrInstance = lazy(() => import('./EhrInstance'));
const EhrInstanceV2Routes = lazy(() =>
  import('./EhrInstanceV2/Routes').then((module) => ({ default: module.EhrInstanceRoutes }))
);
const JobConfigs = lazy(() => import('./EhrInstance/JobConfig'));
const PracticeConfigEhrInstance = lazy(() => import('./EhrInstance/PracticeConfig'));
const Encounters = lazy(() => import('./Encounters'));
const Entity = lazy(() => import('./Entity').then((module) => ({ default: module.Entities })));
const EntityTest = lazy(() => import('./EntityTest'));
const Feeds = lazy(() => import('./Feeds/Router'));
const Findings = lazy(() => import('./Findings'));
const Fingerprint = lazy(() => import('./Fingerprint'));
const Home = lazy(() => import('./Home'));
const Messaging = lazy(() =>
  import('./Messaging/Routes').then((module) => ({
    default: module.MessagingRoutes,
  }))
);
const MindtickleSso = lazy(() => import('./Mindtickle'));
const NoteTemplates = lazy(() => import('./NoteTemplates'));
const PayoutRoutes = lazy(() =>
  import('./Payments/PayoutRoutes').then((module) => ({ default: module.PayoutRoutes }))
);
const PracticeConfigs = lazy(() => import('./PracticeConfig/Route'));
const FormTemplates = lazy(() => import('./PrevisitFormTemplate'));
const ProviderRoutes = lazy(() =>
  import('./Providers/Routes').then((module) => ({ default: module.ProviderRoutes }))
);
const RecommendationTemplates = lazy(() => import('./RecommendationTemplates'));
const PrevisitLinks = lazy(() => import('./PrevisitLink'));
const Reports = lazy(() => import('./Reports'));
const TasksRoutes = lazy(() => import('./Tasks/TasksRoutes'));
const Version = lazy(() => import('./Version'));
const LazyAgents = lazy(() => import('./Agents/index'));
const LazyNotifications = lazy(() => import('./Notifications/index'));
const ExternalDocPackets = lazy(() =>
  import('./ExternalDocPackets').then((module) => ({ default: module.ExternalDocPackets }))
);
const ImpersonationPage = lazy(() =>
  import('./Impersonation/Impersonation').then((module) => ({ default: module.ImpersonationPage }))
);
const OrganizationRoutes = lazy(() =>
  import('./Organizations/Routes').then((module) => ({ default: module.OrganizationRoutes }))
);
const CanonicalFieldsForType = lazy(() =>
  import('./OrchestratorInstance/CanonicalFields').then((module) => ({
    default: module.CanonicalFieldsForType,
  }))
);
const DecisionTreeRoutes = lazy(() =>
  import('./DecisionTree/Routes').then((module) => ({ default: module.DecisionTreeRoutes }))
);
const Triggers = lazy(() =>
  import('./Triggers/Routes').then((module) => ({ default: module.TriggersRoutes }))
);
const PaymentPage = lazy(() =>
  import('./Payments/ReportV2/Page').then((module) => ({ default: module.PaymentPage }))
);
const DocumentUpload = lazy(() =>
  import('./DocumentUpload').then((module) => ({ default: module.DocumentUpload }))
);
const StripeAccountRoutes = lazy(() =>
  import('./StripeAccounts/Routes').then((module) => ({ default: module.StripeAccountRoutes }))
);
const NoteRoutes = lazy(() => import('./Notes/index'));
const Practices = lazy(() => import('./Practices'));
const ServiceAccounts = lazy(() => import('./ServiceAccounts/index'));
const PermissionGroupsRoutes = lazy(() =>
  import('./PermissionGroups/Routes').then((module) => ({ default: module.PermissionGroupRoutes }))
);

const EhrSystemRoutes = lazy(() =>
  import('./EhrSystem/Routes').then((module) => ({ default: module.EhrSystemsRoutes }))
);

const KnowledgeBase = lazy(() =>
  import('./KnowledgeBase/Routes').then((module) => ({ default: module.KnowledgeBaseRoutes }))
);

const Payors = lazy(() => import('./Payors/index'));
const UserRoutes = lazy(() =>
  import('./Users/Routes').then((module) => ({ default: module.UserRoutes }))
);
const FrontDeskDevicesRoutes = lazy(() =>
  import('./FrontDeskDevices/Routes').then((module) => ({ default: module.FrontDeskDevicesRoutes }))
);
const FlowWorkRoutes = lazy(() =>
  import('./Agents/Flows/Work/Routes').then((module) => ({ default: module.FlowWorkRoutes }))
);
const HumanTaskRoutes = lazy(() =>
  import('./Agents/Flows/HumanTasks/Routes').then((module) => ({ default: module.HumanTaskRoutes }))
);
const HumanTaskCategoryRoutes = lazy(() =>
  import('./Agents/Flows/HumanTaskCategories/Routes').then((module) => ({
    default: module.HumanTaskCategoryRoutes,
  }))
);
const PhoneNumberConfiguration = lazy(() =>
  import('./PhoneNumberConfiguration/Routes').then((module) => ({
    default: module.PhoneNumberConfigurationRoutes,
  }))
);
const VoiceAssistants = lazy(() =>
  import('./VoiceAssistants/Routes').then((module) => ({
    default: module.VoiceAssistantsRoutes,
  }))
);

export function AppRoutes() {
  const { pathname, search } = useLocation();

  const ehrInstancesV2 = useFlag({
    flagKey: FeatureFlagKey.ehrInstancesV2,
  });

  const apiInstancesV2 = useFlag({
    flagKey: FeatureFlagKey.apiInstancesV2,
  });

  const connectorsOptions = useFlag({
    flagKey: FeatureFlagKey.connectorsOptions,
  });

  const triggerOptions = useFlag({
    flagKey: FeatureFlagKey.triggerOptions,
  });

  return (
    <LazyLoader>
      <Switch>
        {/**
         * When clicking a link that goes up a level, such as `to="..", the browser will append a trailing slash
         * This breaks code like `${match.path}/flows/definitions/:id` because will end up with a double slash.
         * This solution works well.
         * The Redirect replaces the url with the same url but without the trailing slash. It also preserves the search params.
         * https://jasonwatmore.com/post/2020/03/26/react-router-relative-links-example
         */}
        <Redirect
          from="/:url*(/+)"
          to={{
            pathname: pathname.slice(0, -1),
            search,
          }}
        />
        <Route
          path="/api-instances"
          component={apiInstancesV2.isEnabled ? ApiInstanceV2Routes : ApiInstance}
        />
        <ProtectedRoute
          path="/appointment-kinds"
          component={() => <CanonicalFieldsForType canonicalFieldTypeName="AppointmentKind" />}
          permissionRequirement={{
            and: [
              api.Group.OrchestratorPermission.ViewCanonicalFieldTypes,
              api.Group.OrchestratorPermission.ViewCanonicalFields,
              api.Group.OrchestratorPermission.ViewAppointmentKinds,
            ],
          }}
        />
        <Route path="/contact-status" component={ContactStatusRoutes} />
        {connectorsOptions.isListScreenEnabled && (
          <Route path="/connectors" component={ConnectorRoutes} />
        )}
        <Route path="/decision-trees" component={DecisionTreeRoutes} />
        <Route path="/recommendation-templates" component={RecommendationTemplates} />
        <Route path="/previsit-links" component={PrevisitLinks} />
        <Route path="/diagnostics" component={Diagnostics} />
        <Route path="/document-upload" component={DocumentUpload} />
        <Route path="/document-templates" component={DocumentTemplates} />
        <Route
          path="/ehr-instances"
          component={ehrInstancesV2.isEnabled ? EhrInstanceV2Routes : EhrInstance}
        />
        <Route path="/encounters" component={Encounters} />
        <Route path="/entity" component={Entity} />
        <Route path={entityTestPath()} component={EntityTest} />
        <Route path="/external-doc-packets" component={ExternalDocPackets} />
        <Route path="/feeds" component={Feeds} />
        <Route path="/findings" component={Findings} />
        {triggerOptions.isEnabled && <Route path="/triggers" component={Triggers} />}
        <Route path="/impersonation" component={ImpersonationPage} />
        <Route path="/job-configs" component={JobConfigs} />
        <Route path="/note-templates" component={NoteTemplates} />
        <Route path="/notifications" component={LazyNotifications} />
        <Route path="/fingerprint" component={Fingerprint} />
        <Route path="/messaging" component={Messaging} />
        <Route path="/assistant" component={Assistants} />
        <Route path="/assistants" component={VoiceAssistants} />
        <Route path="/payments/report" component={PaymentPage} />
        <Route path="/payouts" component={PayoutRoutes} />
        <ProtectedRoute
          path="/phone-numbers"
          component={PhoneNumberConfiguration}
          permissionRequirement={api.Group.Permission.ViewAssistantPhoneConfiguration}
        />
        <Route path="/organizations" component={OrganizationRoutes} />
        <Route path="/practice-configs" component={PracticeConfigs} />
        <Route path="/practice-config-ehr-instances" component={PracticeConfigEhrInstance} />
        <Route path="/practice-config-api-instances" component={PracticeConfigApiInstance} />
        <Route path="/previsit-form-templates" component={FormTemplates} />
        <Route path="/reports" component={Reports} />
        <Route path="/tasks" component={TasksRoutes} />
        <Route path="/providers" component={ProviderRoutes} />
        <Route path="/version" component={Version} />
        <Route path="/mindtickle-sso" component={MindtickleSso} />
        <Route path="/agents" component={LazyAgents} />
        <Route path="/stripe-accounts" component={StripeAccountRoutes} />
        <Route path="/users" component={UserRoutes} />
        <Route path="/notes" component={NoteRoutes} />
        <Route path="/practices" component={Practices} />
        <Route path="/permission-groups" component={PermissionGroupsRoutes} />
        <Route path="/payors" component={Payors} />
        <Route path="/ehr-systems" component={EhrSystemRoutes} />
        <Route path="/front-desk-devices" component={FrontDeskDevicesRoutes} />
        <Route exact path="/" component={Home} />
        <Route path="/service-accounts" component={ServiceAccounts} />
        <Route path="/knowledge-base" component={KnowledgeBase} />
        <Route path="/front-desk-devices" component={FrontDeskDevicesRoutes} />
        <Route path="/signals" component={FlowWorkRoutes} />
        <Route
          path="/human-tasks"
          component={() => <HumanTaskRoutes appVariant={HumanTaskAppVariant.StaffApp} />}
        />
        <Route path="/human-task-categories" component={HumanTaskCategoryRoutes} />
        <Route path="*" component={NotFoundPage} />
      </Switch>
    </LazyLoader>
  );
}
